import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

export interface User {
  id: string
  email: string
  name: string
  role: 'iso' | 'broker' | 'admin' | 'master_admin'
  status: 'active' | 'pending' | 'suspended' | 'rejected'
  lastLogin?: Date
  createdAt: Date
  updatedAt: Date
}

export interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<{ success: boolean; message: string }>
  logout: () => Promise<void>
  checkAuthStatus: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const isAuthenticated = !!user

  const checkAuthStatus = async (): Promise<void> => {
    try {
      console.log('🔍 Starting auth status check...')
      setIsLoading(true)
      const response = await fetch('/api/auth/status', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const data = await response.json()
        console.log('🔍 AuthContext Status Check:', {
          responseData: data,
          isAuthenticated: data.isAuthenticated,
          user: data.user,
          userRole: data.user?.role,
          timestamp: new Date().toISOString()
        })
        if (data.isAuthenticated && data.user) {
          setUser(data.user)
        } else {
          setUser(null)
        }
      } else {
        setUser(null)
      }
    } catch (error) {
      console.error('Auth status check failed:', error)
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (email: string, password: string): Promise<{ success: boolean; message: string }> => {
    try {
      setIsLoading(true)
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        console.log('🔍 AuthContext Login Success:', {
          responseData: data,
          user: data.user,
          userRole: data.user?.role,
          timestamp: new Date().toISOString()
        })
        setUser(data.user)
        return { success: true, message: data.message || 'Login successful' }
      } else {
        setUser(null)
        return { 
          success: false, 
          message: data.message || data.error || 'Login failed' 
        }
      }
    } catch (error) {
      console.error('Login error:', error)
      setUser(null)
      return { 
        success: false, 
        message: 'Network error. Please try again.' 
      }
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true)
      
      console.log('🚪 Starting complete logout process...', {
        currentUser: user ? { email: user.email, role: user.role } : null,
        timestamp: new Date().toISOString()
      })
      
      // Call backend logout to destroy session
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      
      const data = response.ok ? await response.json() : null
      console.log('🔙 Backend logout response:', { status: response.status, data })
      
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Clear ALL frontend state aggressively
      console.log('🧹 Clearing ALL frontend auth state...')
      
      // Clear React state immediately
      setUser(null)
      setIsLoading(false)
      
      // Clear all browser storage
      try {
        localStorage.clear()
        sessionStorage.clear()
        console.log('✅ Browser storage cleared')
      } catch (storageError) {
        console.error('Storage clear error:', storageError)
      }
      
      // Clear all cookies more aggressively
      try {
        // Get all cookies and clear them
        const cookies = document.cookie.split(";")
        cookies.forEach((cookie) => {
          const eqPos = cookie.indexOf("=")
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim()
          
          // Clear with different path variations
          const clearOptions = [
            { path: '/' },
            { path: '/', domain: window.location.hostname },
            { path: '/', domain: `.${window.location.hostname}` },
            {}
          ]
          
          clearOptions.forEach(options => {
            const optionsStr = Object.entries(options)
              .map(([key, value]) => `${key}=${value}`)
              .join(';')
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;${optionsStr}`
          })
        })
        
        // Also clear known session cookie names explicitly
        const knownCookies = ['connect.sid', 'iso.portal.sid']
        knownCookies.forEach(cookieName => {
          document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
          document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`
        })
        
        console.log('✅ All cookies cleared')
      } catch (cookieError) {
        console.error('Cookie clear error:', cookieError)
      }
      
      console.log('🔄 Forcing complete page reload to clear all state...')
      
      // Force a complete reload to clear React Router state and any other cached state
      window.location.replace('/login')
    }
  }

  // Check auth status on mount
  useEffect(() => {
    checkAuthStatus()
  }, [])

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    checkAuthStatus,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext